import React, { useState, useRef, useEffect } from "react";
import styles from "./styles.module.css";
import Button from "../../button-v1";
import { toast } from "react-hot-toast";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPatientDocuments } from "../../../services/patient/documents";

const UploadFiles = ({
  accept = ".docx, .pdf",
  multiple = false,
  maxFiles = 1,
  onFilesChange,
  onFilesRemove,
  placeholder = "Drop files here",
  subText = "Or",
  buttonText = "Browse",
  className = "",
  disabled = false,
  id = "file_input",
  name = "file_input",
  showFileList = false,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  allowedFileTypes = [],
  customValidation = null,
  filesHandler,
  onSuccess,
  options,
  variant = "default",
}) => {
  const [queryParams] = useSearchParams();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [documentType, setDocumentType] = useState("");
  const [documents, setDocuments] = useState([]);

  const CCM = Boolean(Number.parseInt(queryParams.get("CCM") || "0"));
  const RPM = Boolean(Number.parseInt(queryParams.get("RPM") || "0"));

  const patientDocumentsQuery = useQuery(["patient-documents"], () =>
    getPatientDocuments()
  );

  const isChatbotVariant = variant === "chatbot";

  const getDocumentTypes = () => {
    if (options) {
      return options;
    }

    if (RPM && CCM) {
      return [
        { label: "Doctor Notes", value: "doctor" },
        { label: "Careplan", value: "careplan" },
        { label: "Consent", value: "consent" },
        { label: "Additional", value: "additional" },
        { label: "Lab Reports", value: "lab" },
        { label: "Imaging", value: "imaging" },
      ];
    }

    if (RPM) {
      return [
        { label: "Consent", value: "consent" },
        { label: "Additional", value: "additional" },
        { label: "Lab Reports", value: "lab" },
        { label: "Imaging", value: "imaging" },
      ];
    }

    if (CCM) {
      return [
        { label: "Doctor Notes", value: "doctor" },
        { label: "Careplan", value: "careplan" },
        { label: "Additional", value: "additional" },
        { label: "Lab Reports", value: "lab" },
        { label: "Imaging", value: "imaging" },
      ];
    }

    return [];
  };

  const documentTypes = getDocumentTypes();
  const defaultType = documentTypes[0]?.value || "";

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxFileSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxFileSize / (1024 * 1024)}MB limit`,
      };
    }

    // Check file type if allowedFileTypes is specified
    if (allowedFileTypes.length > 0) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!allowedFileTypes.includes(fileExtension)) {
        return {
          valid: false,
          error: `File type .${fileExtension} is not allowed`,
        };
      }
    }

    // Custom validation if provided
    if (customValidation) {
      const customResult = customValidation(file);
      if (!customResult.valid) {
        return customResult;
      }
    }

    return { valid: true };
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];

    fileArray.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (errors.length > 0) {
      console.error("File validation errors:", errors);
      const primaryError = errors[0];
      const remainingCount = errors.length - 1;
      toast.error(
        remainingCount > 0
          ? `${primaryError} (+${remainingCount} more)`
          : primaryError
      );
    }

    if (validFiles.length > 0) {
      if (isChatbotVariant) {
        const incomingDocuments = validFiles.map((file) => ({
          file,
          type: defaultType,
        }));

        const uniqueNewDocs = incomingDocuments.filter(
          (newDoc) =>
            !documents.some(
              (doc) =>
                doc.file.name === newDoc.file.name &&
                doc.file.size === newDoc.file.size &&
                doc.file.lastModified === newDoc.file.lastModified
            )
        );

        const remainingSlots = maxFiles - documents.length;
        if (uniqueNewDocs.length > remainingSlots) {
          toast.error(
            documents.length >= maxFiles
              ? `You can upload a maximum of ${maxFiles} PDF files. Please remove one before adding more.`
              : `You can upload a maximum of ${maxFiles} PDF files. Only ${remainingSlots} more can be added.`
          );
        }

        if (remainingSlots > 0 && uniqueNewDocs.length > 0) {
          const docsToAdd = uniqueNewDocs.slice(0, remainingSlots);
          setDocuments((prev) => [...prev, ...docsToAdd]);
        }

        fileInputRef.current.value = "";
        return;
      }

      const newFiles = multiple
        ? [...uploadedFiles, ...validFiles].slice(0, maxFiles)
        : validFiles.slice(0, 1);

      setUploadedFiles(newFiles);
      onFilesChange?.(newFiles);
    }
  };

  const handleChange = (e) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
      e.target.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files) {
      handleFiles(files);
    }
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDragActive(false);
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const getDropZoneClassName = () => {
    let className = styles.drop_zone;
    if (dragActive) className += ` ${styles.drag_active}`;
    if (disabled) className += ` ${styles.disabled}`;
    return className;
  };

  const handelAddDocument = () => {
    const documentFound = documents.find(
      (_doc) =>
        _doc.file.name === uploadedFiles[0].name && _doc.type === documentType
    );
    if (documentFound) {
      toast.error("Document already exists");
      fileInputRef.current.value = "";
      setUploadedFiles([]);
      setDocumentType("");
      return;
    }

    const newDocument = {
      file: uploadedFiles[0],
      type: documentType,
    };

    setDocuments((prev) => [...prev, newDocument]);
    fileInputRef.current.value = "";
    setUploadedFiles([]);
    setDocumentType("");
  };

  const HandleRemove = (doc) =>
    setDocuments((prev) =>
      prev.filter(
        (_doc) => !(_doc.file.name === doc.file.name && _doc.type === doc.type)
      )
    );

  const updateDocumentType = (targetDoc, newType) => {
    setDocuments((prev) =>
      prev.map((doc) => {
        const isMatch =
          doc.file.name === targetDoc.file.name &&
          doc.file.size === targetDoc.file.size &&
          doc.file.lastModified === targetDoc.file.lastModified;

        return isMatch ? { ...doc, type: newType } : doc;
      })
    );
  };

  useEffect(() => {
    filesHandler(documents);
  }, [documents]);

  useEffect(() => {
    if (onSuccess) {
      setDocuments([]);
    }
  }, [onSuccess]);

  return (
    <div
      className={`${styles.upload_wrapper} ${styles[`variant_${variant}`] || ""} ${className}`}
    >
      <div
        className={getDropZoneClassName()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          id={id}
          name={name}
          onChange={handleChange}
          multiple={multiple}
          style={{ display: "none" }}
          disabled={disabled}
        />

        <div className={styles.inner_drop_zone}>
          {isChatbotVariant ? (
            <>
                     <div className={styles.drop_zone_right}>
                <img src="/assets/images/blue-upload-cloud.png" alt="Cloud" />
                <p>{placeholder}</p>
              </div>
              <p className={styles.variant_chatbot_or_text}>OR</p>
              <div className={styles.drop_zone_right}>
                <Button disabled={disabled}>{buttonText}</Button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.drop_zone_right}>
                <img src="/assets/images/blue-upload-cloud.png" alt="Cloud" />
                {!multiple && uploadedFiles.length === 1 ? (
                  <p>{uploadedFiles[0].name}</p>
                ) : (
                  <>
                    <p>{placeholder}</p>
                    <p>{subText}</p>
                  </>
                )}
              </div>

              <div className={styles.drop_zone_right}>
                {!multiple && <Button disabled={disabled}>{buttonText}</Button>}
              </div>
            </>
          )}
        </div>
      </div>

      {!isChatbotVariant && (
        <section className={styles.input_container}>
          <div className={styles.input_field}>
            <label>Document Type</label>
            <Select
              onChange={(value) => setDocumentType(value)}
              value={documentType}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type">
                  {documentTypes?.find((type) => type.value === documentType)
                    ?.label ?? "Select type"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {documentTypes?.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            className={styles.action_button}
            onClick={() => handelAddDocument()}
            disabled={uploadedFiles?.length <= 0 || !documentType}
          >
            Add Document
          </Button>
        </section>
      )}

      {isChatbotVariant && (
        <p className={styles.variant_chatbot_hint}>
          Only .PDF files, max {maxFileSize / (1024 * 1024)}MB each. You can upload up to {maxFiles} {maxFiles === 1 ? "file" : "files"}.
        </p>
      )}

      {showFileList && documents.length > 0 && !isChatbotVariant && (
        <section className={styles.container}>
          <header className={styles.containerHeader}>
            <p>Documents</p>
          </header>
          <main className={styles.devicesContainer}>
            <main className={styles.devicesListing}>
              {documents?.map((_doc, index) => (
                <section key={`${_doc.file.name}-${_doc.type}-${index}`} className={styles.device}>
                  <h5>{index + 1}</h5>
                  <section className={styles.deviceInput}>
                    <h5>Name</h5>
                    <p>{_doc.file.name}</p>
                  </section>
                  <section className={styles.deviceInput}>
                    <h5>Type</h5>
                    <p>{_doc.type}</p>
                  </section>
                  <section className={styles.deviceInput}>
                    <FontAwesomeIcon
                      className={styles.action}
                      icon={faTrash}
                      onClick={() => HandleRemove(_doc)}
                    />
                  </section>
                </section>
              ))}
            </main>
          </main>
        </section>
      )}

      {showFileList && isChatbotVariant && documents.length > 0 && (
        <section className={styles.variant_chatbot_list}>
          <h4 className={styles.variant_chatbot_heading}>Uploaded Documents</h4>
          <div className={styles.variant_chatbot_items}>
            {documents.map((_doc, index) => (
              <div
                key={`${_doc.file.name}-${_doc.file.size}-${index}`}
                className={styles.variant_chatbot_item}
              >
                <div className={styles.variant_chatbot_doc_main}>
                  <div >
                    <img src="/assets/images/pdficon.svg" alt="PDF" />
                  </div>
                  <div className={styles.variant_chatbot_doc_meta}>
                    <div className={styles.variant_chatbot_doc_name}>{_doc.file.name}</div>
                    <div className={styles.variant_chatbot_doc_ext}>PDF</div>
                  </div>
                </div>

                <div className={styles.variant_chatbot_doc_actions}>
                  <Select
                    onChange={(value) => updateDocumentType(_doc, value)}
                    value={_doc.type}
                  >
                    <SelectTrigger className={styles.variant_chatbot_select_trigger}>
                      <SelectValue className={styles.variant_chatbot_select_value} placeholder="Select type">
                        {documentTypes?.find((type) => type.value === _doc.type)
                          ?.label ?? "Select type"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes?.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <button
                    type="button"
                    className={styles.variant_chatbot_remove}
                    onClick={() => HandleRemove(_doc)}
                    aria-label="Remove file"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default UploadFiles;
